import React, { useEffect, useState } from "react"
import axios from "axios"
import { useParams } from "react-router-dom"
import { useSelector, connect } from "react-redux"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import config from "config/config"
import SnackbarComponent from "components/SnackBar"
import { setBreadcrumbItems } from "../../store/actions"

const statusColors = {
  ai_generated: "secondary",
  pending_review: "warning",
  approved: "info",
  publishing: "info",
  published: "success",
  rejected: "danger",
  cancelled: "dark",
  failed: "danger",
  discontinued: "dark",
  completed: "info",
}

const PostCard = ({ post, authHeader, onChanged }) => {
  const [caption, setCaption] = useState(post.caption || "")
  const [imagePrompt, setImagePrompt] = useState(post.aiMetadata?.imagePrompt || "")
  const [captionPrompt, setCaptionPrompt] = useState(post.aiMetadata?.captionPrompt || "")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    setCaption(post.caption || "")
  }, [post.caption])

  useEffect(() => {
    setImagePrompt(post.aiMetadata?.imagePrompt || "")
    setCaptionPrompt(post.aiMetadata?.captionPrompt || "")
  }, [post.aiMetadata?.imagePrompt, post.aiMetadata?.captionPrompt])

  const canEdit = !["approved", "publishing", "published"].includes(post.status)

  const generateContent = async () => {
    setBusy(true)
    setErr(null)
    try {
      const response = await axios.post(
        `${config.backendUrl}/social-media-campaigns/posts/${post.id}/generate-content`,
        { imagePrompt, captionPrompt },
        { headers: authHeader },
      )
      onChanged(response.data.post)
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to generate content.")
    } finally {
      setBusy(false)
    }
  }

  const saveCaption = async () => {
    setBusy(true)
    setErr(null)
    try {
      const response = await axios.patch(
        `${config.backendUrl}/social-media-campaigns/posts/${post.id}`,
        { caption },
        { headers: authHeader },
      )
      onChanged(response.data.post)
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to save caption.")
    } finally {
      setBusy(false)
    }
  }

  const replaceImage = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    setBusy(true)
    setErr(null)
    try {
      const formData = new FormData()
      formData.append("file", file)
      const uploadResponse = await axios.post(
        `${config.backendUrl}/aws/file?email=reputation-management`,
        formData,
      )

      const response = await axios.patch(
        `${config.backendUrl}/social-media-campaigns/posts/${post.id}`,
        { media: [uploadResponse.data.url] },
        { headers: authHeader },
      )
      onChanged(response.data.post)
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to upload image.")
    } finally {
      setBusy(false)
    }
  }

  const cancelPost = async () => {
    setBusy(true)
    setErr(null)
    try {
      const response = await axios.delete(
        `${config.backendUrl}/social-media-campaigns/posts/${post.id}`,
        { headers: authHeader },
      )
      onChanged(response.data.post)
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to cancel post.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <Badge color="light" className="text-dark me-2" pill>
              {post.platform}
            </Badge>
            <Badge color={statusColors[post.status] || "secondary"}>
              {post.status.replace("_", " ")}
            </Badge>
          </div>
          <small className="text-muted">
            {new Date(post.scheduled_time * 1000).toLocaleString()}
          </small>
        </div>

        {err && <p className="text-danger">{err}</p>}

        <Row>
          <Col md={4}>
            {post.media?.[0] ? (
              <>
                <img
                  src={post.media[0]}
                  alt="Post visual"
                  style={{ width: "100%", borderRadius: 8, objectFit: "cover" }}
                />
                {post.aiMetadata?.referenceScreenshotFailed && (
                  <small className="text-warning d-block mt-1">
                    Couldn't capture the reference URL's screenshot - the
                    image doesn't include it.
                  </small>
                )}
              </>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center text-muted text-center p-2"
                style={{
                  height: 160,
                  border: "1px dashed #ccc",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              >
                {post.aiMetadata?.imageCapSkipped
                  ? "Image generation capped for this campaign (testing mode) - upload one manually if needed."
                  : "No image yet"}
              </div>
            )}
            {canEdit && (
              <Input
                type="file"
                accept="image/*"
                className="mt-2"
                disabled={busy}
                onChange={replaceImage}
              />
            )}
          </Col>
          <Col md={8}>
            {post.caption || canEdit ? (
              <Input
                type="textarea"
                rows={4}
                value={caption}
                disabled={!canEdit || busy}
                onChange={e => setCaption(e.target.value)}
                placeholder="Caption will appear here once generated."
              />
            ) : (
              <p className="text-muted">No caption yet.</p>
            )}

            {(post.aiMetadata?.imagePrompt || post.aiMetadata?.captionPrompt || canEdit) && (
              <details className="mt-2">
                <summary className="text-muted" style={{ cursor: "pointer", fontSize: 13 }}>
                  AI prompts used for this post
                </summary>
                <div className="mt-2">
                  <Label className="mb-1" style={{ fontSize: 12 }}>
                    Image prompt
                  </Label>
                  <Input
                    type="textarea"
                    rows={3}
                    value={imagePrompt}
                    disabled={!canEdit || busy}
                    onChange={e => setImagePrompt(e.target.value)}
                    placeholder="Leave blank to use the campaign's default image prompt."
                  />
                  <Label className="mb-1 mt-2" style={{ fontSize: 12 }}>
                    Caption prompt
                  </Label>
                  <Input
                    type="textarea"
                    rows={3}
                    value={captionPrompt}
                    disabled={!canEdit || busy}
                    onChange={e => setCaptionPrompt(e.target.value)}
                    placeholder="Leave blank to use the campaign's default caption prompt."
                  />
                  <small className="text-muted d-block mt-1">
                    Edit either prompt and click Regenerate below to have AI
                    re-attempt this post with your preferences.
                  </small>
                </div>
              </details>
            )}

            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={busy || ["approved", "publishing", "published"].includes(post.status)}
                onClick={generateContent}
              >
                {busy ? <Spinner size="sm" /> : post.caption ? "Regenerate" : "Generate Content"}
              </Button>
              {canEdit && post.caption !== caption && (
                <Button size="sm" color="secondary" disabled={busy} onClick={saveCaption}>
                  Save Caption
                </Button>
              )}
              {!["publishing", "published", "cancelled"].includes(post.status) && (
                <Button size="sm" color="danger" outline disabled={busy} onClick={cancelPost}>
                  Cancel
                </Button>
              )}
              {post.status === "published" && post.postUrl && (
                <Button
                  size="sm"
                  color="primary"
                  outline
                  tag="a"
                  href={post.postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on {post.platform === "facebook" ? "Facebook" : "Instagram"}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

const SocialCampaignDetails = props => {
  const { id } = useParams()
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const authHeader = { Authorization: `Bearer ${userData.token}` }

  const [campaign, setCampaign] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const breadcrumbItems = [
    { title: `${activeOrganization.name}`, link: "#" },
    { title: "Social Media Campaigns", link: "/dashboard/social-campaigns" },
    { title: campaign?.campaignTitle || "Details", link: "#" },
  ]

  useEffect(() => {
    if (campaign) {
      props.setBreadcrumbItems(campaign.campaignTitle, breadcrumbItems)
    }
  }, [campaign])

  const fetchCampaign = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    setErr(null)
    try {
      const response = await axios.get(
        `${config.backendUrl}/social-media-campaigns/${id}`,
        { headers: authHeader, skipLoader: silent },
      )
      setCampaign(response.data.campaign)
    } catch (error) {
      if (!silent) {
        setErr(error.response?.data?.message || "Failed to fetch campaign.")
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    if (id) fetchCampaign()
  }, [id])

  const postsStillGenerating = (campaign?.SocialMediaPosts || []).some(
    p => p.status === "ai_generated" && !p.caption,
  )

  // A background worker fills in caption/image one post at a time after
  // creation; poll while any are still empty so they appear without a
  // manual refresh.
  useEffect(() => {
    if (!postsStillGenerating) return

    const interval = setInterval(() => fetchCampaign({ silent: true }), 5000)
    return () => clearInterval(interval)
  }, [postsStillGenerating])

  const handlePostChanged = updatedPost => {
    setCampaign(prev => ({
      ...prev,
      SocialMediaPosts: prev.SocialMediaPosts.map(p =>
        p.id === updatedPost.id ? updatedPost : p,
      ),
    }))
  }

  const [discontinuing, setDiscontinuing] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [success, setSuccess] = useState(null)
  const [activeTab, setActiveTab] = useState("posts")

  const discontinueCampaign = async () => {
    if (
      !window.confirm(
        "Cancel this campaign? Any posts not yet published will be cancelled.",
      )
    ) {
      return
    }

    setDiscontinuing(true)
    setErr(null)
    try {
      await axios.post(
        `${config.backendUrl}/social-media-campaigns/${id}/discontinue`,
        {},
        { headers: authHeader },
      )
      fetchCampaign()
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to cancel campaign.")
    } finally {
      setDiscontinuing(false)
    }
  }

  const hasPendingReview = (campaign?.SocialMediaPosts || []).some(
    p => p.status === "pending_review",
  )

  const acceptCampaignPlan = async () => {
    setAccepting(true)
    setErr(null)
    try {
      const response = await axios.post(
        `${config.backendUrl}/social-media-campaigns/${id}/approve-all`,
        {},
        { headers: authHeader },
      )
      const { approvedCount, skippedCount } = response.data
      setSuccess(
        skippedCount
          ? `Approved ${approvedCount} post(s). ${skippedCount} are still generating - click Accept again once they're ready.`
          : `Approved ${approvedCount} post(s).`,
      )
      fetchCampaign()
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to accept campaign plan.")
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <React.Fragment>
      <SnackbarComponent err={err} success={success} setErr={setErr} setSuccess={setSuccess} />

      {campaign && (
        <>
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <CardTitle tag="h4">{campaign.campaignTitle}</CardTitle>
                <div className="d-flex gap-2">
                  {hasPendingReview &&
                    !["discontinued", "deleted"].includes(campaign.status) &&
                    userData.role !== "viewer" && (
                      <Button
                        size="sm"
                        color="success"
                        disabled={accepting}
                        onClick={acceptCampaignPlan}
                      >
                        {accepting ? <Spinner size="sm" /> : "Accept Campaign Plan"}
                      </Button>
                    )}
                  {!["discontinued", "deleted", "completed"].includes(campaign.status) && (
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      disabled={discontinuing}
                      onClick={discontinueCampaign}
                    >
                      {discontinuing ? <Spinner size="sm" /> : "Cancel Campaign"}
                    </Button>
                  )}
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Badge color="light" className="text-dark" pill>
                  {(campaign.platforms || []).join(", ")}
                </Badge>
                <Badge color={statusColors[campaign.status] || "secondary"}>
                  {campaign.status}
                </Badge>
                <Badge color="light" className="text-dark">
                  {campaign.startDate?.slice(0, 10)} - {campaign.endDate?.slice(0, 10)}
                </Badge>
              </div>

              <Nav tabs className="mt-3">
                <NavItem>
                  <NavLink
                    active={activeTab === "posts"}
                    style={{ cursor: "pointer" }}
                    onClick={() => setActiveTab("posts")}
                  >
                    Scheduled Posts
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={activeTab === "settings"}
                    style={{ cursor: "pointer" }}
                    onClick={() => setActiveTab("settings")}
                  >
                    Settings
                  </NavLink>
                </NavItem>
              </Nav>
            </CardBody>
          </Card>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="posts">
              {postsStillGenerating && (
                <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                  <Spinner size="sm" />
                  AI is generating content for the remaining posts, one at a
                  time - this page will update automatically.
                </div>
              )}
              {(campaign.SocialMediaPosts || []).map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  authHeader={authHeader}
                  onChanged={handlePostChanged}
                />
              ))}
            </TabPane>

            <TabPane tabId="settings">
              <Card>
                <CardBody>
                  {campaign.campaignDescription && (
                    <div className="mb-3">
                      <small className="text-muted d-block">Description</small>
                      {campaign.campaignDescription}
                    </div>
                  )}
                  {campaign.aiTheme && (
                    <p className="mb-3">
                      <strong>Theme:</strong> {campaign.aiTheme} &nbsp;
                      <strong>Tone:</strong> {campaign.aiTone}
                    </p>
                  )}

                  <div className="d-flex flex-wrap align-items-start gap-4">
                    {campaign.logoUrl && (
                      <div>
                        <small className="text-muted d-block">Logo</small>
                        <img
                          src={campaign.logoUrl}
                          alt="Campaign logo"
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "contain",
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            padding: 3,
                          }}
                        />
                      </div>
                    )}
                    {campaign.imageStyle && (
                      <div>
                        <small className="text-muted d-block">Imagery Style</small>
                        {campaign.imageStyle}
                      </div>
                    )}
                    {campaign.hashtags && (
                      <div>
                        <small className="text-muted d-block">Hashtags</small>
                        {campaign.hashtags}
                      </div>
                    )}
                    <div>
                      <small className="text-muted d-block">Business name in image</small>
                      {campaign.includeOrgNameInImage ? "Yes" : "No"}
                    </div>
                  </div>

                  {campaign.testimonials?.length > 0 && (
                    <div className="mt-3 pt-2 border-top">
                      <small className="text-muted d-block mb-1">
                        Testimonials used ({campaign.includeTestimonialsInImage
                          ? "shown in image"
                          : "caption only"})
                      </small>
                      {campaign.testimonials.map((t, i) => (
                        <p key={i} className="mb-1" style={{ fontSize: 14 }}>
                          "{t.text}" <strong>- {t.name}</strong>
                        </p>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>
        </>
      )}
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(SocialCampaignDetails)
